// src/firebase/services.js
// All Firebase operations: Auth, Firestore, Storage

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
} from "firebase/auth";

import {
  collection, doc, getDoc, getDocs, addDoc, setDoc,
  updateDoc, query, where, orderBy,
  onSnapshot, serverTimestamp, increment, writeBatch,
} from "firebase/firestore";

import { ref, uploadBytes, uploadBytesResumable, getDownloadURL } from "firebase/storage";

import { auth, db, storage } from "./config";

/* ══════════════════════════════════════════════════════════════
   AUTH SERVICES
══════════════════════════════════════════════════════════════ */

// Listen to auth state changes
export const onAuthChange = (callback) => onAuthStateChanged(auth, callback);

// Sign in
export const signIn = async (email, password) => {
  const result = await signInWithEmailAndPassword(auth, email, password);
  return result.user;
};

// Sign out
export const signOutUser = () => signOut(auth);

// Create user account (used by admin when adding HR users)
export const createUser = async (email, password, displayName) => {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(result.user, { displayName });
  return result.user;
};

// Send password reset email
export const resetUserPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

/* ══════════════════════════════════════════════════════════════
   USER PROFILE SERVICES
══════════════════════════════════════════════════════════════ */

// Get user profile from Firestore
export const getUserProfile = async (uid) => {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Create/update user profile
export const saveUserProfile = async (uid, data) => {
  await setDoc(doc(db, "users", uid), {
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// Get all HR users for a company
export const getCompanyUsers = async (companyId) => {
  const q = query(collection(db, "users"), where("companyId", "==", companyId), where("role", "==", "hr"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Get all HR users (admin)
export const getAllUsers = async () => {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Add credits to user
export const addUserCredits = async (uid, amount) => {
  await updateDoc(doc(db, "users", uid), {
    credits: increment(amount),
    updatedAt: serverTimestamp(),
  });
};

// Subscribe to user profile (real-time)
export const subscribeToUser = (uid, callback) =>
  onSnapshot(doc(db, "users", uid), (snap) => {
    if (snap.exists()) callback({ id: snap.id, ...snap.data() });
  });

/* ══════════════════════════════════════════════════════════════
   COMPANY SERVICES
══════════════════════════════════════════════════════════════ */

// Get all companies
export const getCompanies = async () => {
  const snap = await getDocs(collection(db, "companies"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Get one company
export const getCompany = async (companyId) => {
  const snap = await getDoc(doc(db, "companies", companyId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Create company
export const createCompany = async (data) => {
  const ref = await addDoc(collection(db, "companies"), {
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
};

// Update company
export const updateCompany = async (companyId, data) => {
  await updateDoc(doc(db, "companies", companyId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

// Toggle company status
export const toggleCompanyStatus = async (companyId, status) => {
  await updateDoc(doc(db, "companies", companyId), {
    status,
    updatedAt: serverTimestamp(),
  });
};

// Grant/revoke verified badge
export const setCompanyVerified = async (companyId, verified) => {
  await updateDoc(doc(db, "companies", companyId), {
    verified,
    updatedAt: serverTimestamp(),
  });
};

// Add credits to company and all its users
export const addCompanyCredits = async (companyId, amount) => {
  const batch = writeBatch(db);
  // Update company
  batch.update(doc(db, "companies", companyId), {
    credits: increment(amount),
    updatedAt: serverTimestamp(),
  });
  // Update all users of this company
  const usersSnap = await getDocs(query(collection(db, "users"), where("companyId", "==", companyId)));
  usersSnap.docs.forEach(d => {
    batch.update(doc(db, "users", d.id), { credits: increment(amount) });
  });
  await batch.commit();
};

// Subscribe to companies (real-time)
export const subscribeToCompanies = (callback) =>
  onSnapshot(collection(db, "companies"), (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });

/* ══════════════════════════════════════════════════════════════
   EMPLOYEE SERVICES
══════════════════════════════════════════════════════════════ */

// Get all employees
export const getEmployees = async () => {
  const snap = await getDocs(collection(db, "employees"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Search employee by Aadhaar (exact)
export const searchByAadhaar = async (aadhaar) => {
  const q = query(collection(db, "employees"), where("aadhaar", "==", aadhaar));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Search employee by name (partial - Firestore workaround)
export const searchByName = async (name) => {
  const snap = await getDocs(collection(db, "employees"));
  const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return all.filter(e => e.name?.toLowerCase().includes(name.toLowerCase()));
};

// Add employee record
export const addEmployee = async (data) => {
  const ref = await addDoc(collection(db, "employees"), {
    ...data,
    createdAt: serverTimestamp(),
  });
  return ref.id;
};

// Bulk add employees
export const bulkAddEmployees = async (records) => {
  const batch = writeBatch(db);
  records.forEach(r => {
    const ref = doc(collection(db, "employees"));
    batch.set(ref, { ...r, createdAt: serverTimestamp() });
  });
  await batch.commit();
};

// Subscribe to employees (real-time)
export const subscribeToEmployees = (callback) =>
  onSnapshot(collection(db, "employees"), (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });

/* ══════════════════════════════════════════════════════════════
   SEARCH HISTORY SERVICES
══════════════════════════════════════════════════════════════ */

// Add search history entry
export const addSearchHistory = async (data) => {
  await addDoc(collection(db, "searchHistory"), {
    ...data,
    createdAt: serverTimestamp(),
  });
};

// Get search history for a user
export const getUserSearchHistory = async (uid) => {
  const q = query(
    collection(db, "searchHistory"),
    where("userId", "==", uid),
    orderBy("createdAt", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Get all search history (admin)
export const getAllSearchHistory = async () => {
  const q = query(collection(db, "searchHistory"), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Subscribe to search history (real-time, user specific)
export const subscribeToHistory = (uid, callback) => {
  const q = query(
    collection(db, "searchHistory"),
    where("userId", "==", uid),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

export const subscribeToAllHistory = (callback) => {
  const q = query(
    collection(db, "searchHistory"),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

/* ══════════════════════════════════════════════════════════════
   STORAGE SERVICES
══════════════════════════════════════════════════════════════ */

// Upload company logo
export const uploadCompanyLogo = async (companyId, file) => {
  const storageRef = ref(storage, `logos/${companyId}/${file.name}`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  await updateCompany(companyId, { logoUrl: url });
  return url;
};

// Upload CSV file for bulk import record keeping
export const uploadBulkCSV = async (userId, file) => {
  const storageRef = ref(storage, `bulk-uploads/${userId}/${Date.now()}_${file.name}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

// Upload a candidate resume (PDF/DOC). companyId+candidateId scope the storage path
// so Firestore-style storage rules can restrict access to that company only.
// onProgress(percent:number) is called as the upload streams, if provided.
export const uploadResume = (companyId, candidateId, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const safeCompany = companyId || "unassigned";
    const path = `resumes/${safeCompany}/${candidateId}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snap) => {
        if (onProgress) {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          onProgress(pct);
        }
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url, path, name: file.name, size: file.size, uploadedAt: new Date().toISOString() });
        } catch (e) {
          reject(e);
        }
      }
    );
  });
};

// Upload an address verification selfie. addressType is "present" or "permanent".
export const uploadAddressSelfie = (checkId, addressType, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const path = `address-selfies/${checkId}/${addressType}_${Date.now()}.jpg`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snap) => {
        if (onProgress) {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          onProgress(pct);
        }
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url, path });
        } catch (e) {
          reject(e);
        }
      }
    );
  });
};

// Upload an onboarding checklist document (salary slips, PAN, Aadhaar, etc.)
// docKey scopes storage so each document type has its own path per candidate.
export const uploadOnboardingDoc = (companyId, candidateId, docKey, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const safeCompany = companyId || "unassigned";
    const path = `onboarding-docs/${safeCompany}/${candidateId}/${docKey}_${Date.now()}_${file.name}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snap) => {
        if (onProgress) {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          onProgress(pct);
        }
      },
      (err) => reject(err),
      async () => {
        try {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url, path, name: file.name, size: file.size, uploadedAt: new Date().toISOString() });
        } catch (e) {
          reject(e);
        }
      }
    );
  });
};

// Save/overwrite a resume reference against a company's employee file record.
// Called automatically when a candidate moves to the "selected" ATS stage,
// and also usable for manual HR uploads directly to an employee's file.
export const saveEmployeeFileResume = async (companyId, employeeKey, resumeMeta) => {
  const id = `${companyId || "unassigned"}_${employeeKey}`;
  await setDoc(
    doc(db, "employeeFiles", id),
    {
      companyId: companyId || "",
      employeeKey,
      resume: resumeMeta,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return id;
};

// Fetch an employee file record (resume + any other docs stored against it)
export const getEmployeeFile = async (companyId, employeeKey) => {
  const id = `${companyId || "unassigned"}_${employeeKey}`;
  const snap = await getDoc(doc(db, "employeeFiles", id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Save the full set of HR-approved onboarding documents (salary slips, PAN,
// Aadhaar, etc.) into the employee's file. Called only once a candidate has
// fully joined — every document must already be individually approved
// before this runs (see autoCreateEmployeeFileOnJoin in App.js).
export const saveEmployeeFileDocuments = async (companyId, employeeKey, approvedDocs, meta = {}) => {
  const id = `${companyId || "unassigned"}_${employeeKey}`;
  await setDoc(
    doc(db, "employeeFiles", id),
    {
      companyId: companyId || "",
      employeeKey,
      documents: approvedDocs,
      documentsSavedAt: new Date().toISOString(),
      updatedAt: serverTimestamp(),
      ...meta,
    },
    { merge: true }
  );
  return id;
};


// Candidate-uploaded reference-check documents (public page, no login). Path: cv-ref-docs/{requestId}/...
export const uploadRefDoc = (requestId, refNum, docKey, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const safeName = String(file.name || "file").replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `cv-ref-docs/${requestId}/ref${refNum}_${docKey}_${Date.now()}_${safeName}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type || undefined });
    task.on(
      "state_changed",
      (snap) => { if (onProgress) onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)); },
      (err) => reject(err),
      async () => {
        try { resolve({ url: await getDownloadURL(task.snapshot.ref), path }); } catch (e) { reject(e); }
      }
    );
  });
};

// ── In-app notifications (bell icon dropdown) ──
// targetRole: "admin" | "hr" | "all" | "superadmin" — who at the company
// (or platform-wide, for superadmin) should see this.
// targetUserId: optional — if set, ONLY this specific user sees it,
// regardless of targetRole (e.g. "your offer was approved").
// companyId: null for platform-wide/superadmin notifications.
export const createNotification = async ({ companyId = null, targetRole, targetUserId = null, type, title, message, link = null }) => {
  try {
    await addDoc(collection(db, "notifications"), {
      companyId, targetRole, targetUserId, type, title, message, link,
      read: false,
      createdAt: new Date().toISOString(),
    });
  } catch (e) {
    // Notifications should never block the action that triggered them
    console.warn("Failed to create notification:", e);
  }
};

// Fetch all employee file records for a company (Meitho Select "Employee Files" page)
export const getCompanyEmployeeFiles = async (companyId) => {
  const q = query(collection(db, "employeeFiles"), where("companyId", "==", companyId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Fetch all employee file records across every company (Super Admin view)
export const getAllEmployeeFiles = async () => {
  const snap = await getDocs(collection(db, "employeeFiles"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/* ══════════════════════════════════════════════════════════════
   SUPPORT TICKET SERVICES
══════════════════════════════════════════════════════════════ */

// Create a new support ticket
export const createSupportTicket = async (data) => {
  const ref = await addDoc(collection(db, "supportTickets"), {
    ...data,
    status: "open",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    messages: [],
  });
  return ref.id;
};

// Add a message to a ticket
export const addTicketMessage = async (ticketId, message) => {
  const ticketRef = doc(db, "supportTickets", ticketId);
  const snap = await getDoc(ticketRef);
  const existing = snap.data()?.messages || [];
  await updateDoc(ticketRef, {
    messages: [...existing, { ...message, timestamp: new Date().toISOString() }],
    updatedAt: serverTimestamp(),
    status: message.role === "admin" ? "replied" : "open",
  });
};

// Update ticket status
export const updateTicketStatus = async (ticketId, status) => {
  await updateDoc(doc(db, "supportTickets", ticketId), {
    status,
    updatedAt: serverTimestamp(),
  });
};

// Subscribe to all tickets (admin)
export const subscribeToAllTickets = (callback) =>
  onSnapshot(
    query(collection(db, "supportTickets"), orderBy("createdAt", "desc")),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

// Subscribe to tickets for a specific user/company
export const subscribeToUserTickets = (companyId, callback) =>
  onSnapshot(
    query(collection(db, "supportTickets"), where("companyId", "==", companyId), orderBy("createdAt", "desc")),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

// Log notification (Cloud Function handles email sending)
export const sendNotification = async (data) => {
  await addDoc(collection(db, "notifications"), {
    ...data,
    sent: false,
    createdAt: serverTimestamp(),
  });
};

// Low credits alert
export const triggerLowCreditAlert = async (userId, email, credits) => {
  await sendNotification({
    type: "low_credits",
    userId,
    email,
    credits,
    subject: "Low Search Credits — VerifyTrack BGV",
    message: `Your search credits are running low (${credits} remaining). Upload exit employee records to earn more credits.`,
  });
};

// ══════════════════════════════════════════════════════════════
// WALLET SYSTEM
// ══════════════════════════════════════════════════════════════

// Get wallet for a company
export const getWallet = async (companyId) => {
  const ref = doc(db, "wallets", companyId);
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return { id: companyId, balance: 0, totalLoaded: 0, totalSpent: 0 };
};

// Get all wallets (Super Admin)
export const getAllWallets = async () => {
  const snap = await getDocs(collection(db, "wallets"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Top up wallet
export const topUpWallet = async (companyId, amount, platformFee, razorpayId, note="", gstAmount=0) => {
  const walletRef = doc(db, "wallets", companyId);
  const walletSnap = await getDoc(walletRef);
  const current = walletSnap.exists() ? walletSnap.data() : { balance: 0, totalLoaded: 0, totalSpent: 0 };
  const newBalance = (current.balance || 0) + amount;
  
  await setDoc(walletRef, {
    balance: newBalance,
    totalLoaded: (current.totalLoaded || 0) + amount,
    totalSpent: current.totalSpent || 0,
    lastActivity: serverTimestamp(),
    companyId,
  }, { merge: true });

  // Save transaction
  await addDoc(collection(db, "transactions"), {
    companyId,
    type: "topup",
    amount,
    platformFee,
    gstPct: gstAmount > 0 ? 18 : 0,
    gstAmount,
    totalPaid: amount + platformFee + gstAmount,
    status: "success",
    balanceBefore: current.balance || 0,
    balanceAfter: newBalance,
    razorpayId: razorpayId || "",
    note,
    timestamp: serverTimestamp(),
    createdAt: new Date().toISOString(),
  });

  return newBalance;
};

// Deduct from wallet (search cost)
export const deductWallet = async (companyId, amount, searchType, userId, userName, note="") => {
  const walletRef = doc(db, "wallets", companyId);
  const walletSnap = await getDoc(walletRef);
  if (!walletSnap.exists()) throw new Error("Wallet not found");
  const current = walletSnap.data();
  if ((current.balance || 0) < amount) throw new Error("Insufficient wallet balance");
  const newBalance = (current.balance || 0) - amount;

  await updateDoc(walletRef, {
    balance: newBalance,
    totalSpent: (current.totalSpent || 0) + amount,
    lastActivity: serverTimestamp(),
  });

  // Save transaction
  await addDoc(collection(db, "transactions"), {
    companyId,
    type: "search",
    searchType,
    amount: -amount,
    balanceBefore: current.balance || 0,
    balanceAfter: newBalance,
    userId,
    userName,
    note,
    timestamp: serverTimestamp(),
    createdAt: new Date().toISOString(),
  });

  return newBalance;
};

// Deduct from HR allocation
export const deductHRWallet = async (companyId, userId, amount, searchType, userName) => {
  const hrRef = doc(db, "wallets", companyId, "hrAllocations", userId);
  const hrSnap = await getDoc(hrRef);
  if (!hrSnap.exists()) throw new Error("HR allocation not found");
  const current = hrSnap.data();
  if ((current.balance || 0) < amount) throw new Error("Insufficient HR balance");
  const newBalance = (current.balance || 0) - amount;

  await updateDoc(hrRef, {
    balance: newBalance,
    spent: (current.spent || 0) + amount,
    lastActivity: serverTimestamp(),
  });

  // Save transaction
  await addDoc(collection(db, "transactions"), {
    companyId,
    type: "hr_search",
    searchType,
    amount: -amount,
    balanceBefore: current.balance || 0,
    balanceAfter: newBalance,
    userId,
    userName,
    timestamp: serverTimestamp(),
    createdAt: new Date().toISOString(),
  });

  return newBalance;
};

// Get HR allocation
export const getHRAllocation = async (companyId, userId) => {
  const ref = doc(db, "wallets", companyId, "hrAllocations", userId);
  const snap = await getDoc(ref);
  if (snap.exists()) return { id: snap.id, ...snap.data() };
  return { balance: 0, allocated: 0, spent: 0 };
};

// Get all HR allocations for a company
export const getCompanyHRAllocations = async (companyId) => {
  const snap = await getDocs(collection(db, "wallets", companyId, "hrAllocations"));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Set HR allocation (admin allocates to HR)
export const setHRAllocation = async (companyId, userId, amount, companyWalletBalance) => {
  const hrRef = doc(db, "wallets", companyId, "hrAllocations", userId);
  const hrSnap = await getDoc(hrRef);
  const current = hrSnap.exists() ? hrSnap.data() : { balance: 0, allocated: 0, spent: 0 };
  
  // Update HR allocation
  await setDoc(hrRef, {
    ...current,
    allocated: amount,
    balance: amount,
    spent: 0,
    userId,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// Reverse HR allocation back to pool
export const reverseHRAllocation = async (companyId, userId) => {
  const hrRef = doc(db, "wallets", companyId, "hrAllocations", userId);
  const hrSnap = await getDoc(hrRef);
  if (!hrSnap.exists()) return;
  const current = hrSnap.data();
  const remainingBalance = current.balance || 0;

  // Add remaining back to company wallet
  const walletRef = doc(db, "wallets", companyId);
  const walletSnap = await getDoc(walletRef);
  if (walletSnap.exists()) {
    await updateDoc(walletRef, {
      balance: (walletSnap.data().balance || 0) + remainingBalance,
    });
  }

  // Reset HR allocation
  await updateDoc(hrRef, { balance: 0, allocated: 0 });

  // Log transaction
  await addDoc(collection(db, "transactions"), {
    companyId,
    type: "hr_reverse",
    amount: remainingBalance,
    userId,
    note: "HR allocation reversed to company pool",
    timestamp: serverTimestamp(),
    createdAt: new Date().toISOString(),
  });
};

// Get transactions for a company
export const getCompanyTransactions = async (companyId) => {
  const q = query(
    collection(db, "transactions"),
    where("companyId", "==", companyId),
    orderBy("timestamp", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Get all transactions (Super Admin)
export const getAllTransactions = async () => {
  const q = query(collection(db, "transactions"), orderBy("timestamp", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

// Manual wallet adjustment (Super Admin)
export const manualWalletAdjust = async (companyId, amount, note, adminName, extra = {}) => {
  const walletRef = doc(db, "wallets", companyId);
  const walletSnap = await getDoc(walletRef);
  const current = walletSnap.exists() ? walletSnap.data() : { balance: 0, totalLoaded: 0, totalSpent: 0 };
  const newBalance = Math.max(0, (current.balance || 0) + amount);

  await setDoc(walletRef, {
    balance: newBalance,
    totalLoaded: amount > 0 ? (current.totalLoaded || 0) + amount : current.totalLoaded || 0,
    lastActivity: serverTimestamp(),
  }, { merge: true });

  await addDoc(collection(db, "transactions"), {
    companyId,
    type: "manual",
    amount,
    balanceBefore: current.balance || 0,
    balanceAfter: newBalance,
    note,
    adminName,
    ...extra,
    timestamp: serverTimestamp(),
    createdAt: new Date().toISOString(),
  });

  return newBalance;
};

// Record a failed online wallet recharge (no wallet change) so it appears in
// the recharge history with a "Failed" receipt.
export const recordFailedTopup = async (companyId, amount, gstAmount, reason, paymentId, userName) => {
  await addDoc(collection(db, "transactions"), {
    companyId,
    type: "topup_failed",
    status: "failed",
    amount,
    gstPct: 18,
    gstAmount,
    totalPaid: 0,
    failReason: reason || "",
    razorpayId: paymentId || "",
    userName: userName || "",
    timestamp: serverTimestamp(),
    createdAt: new Date().toISOString(),
  });
};
